import CommonFunctions from "./CommonFunctions";

interface DropdownModel extends kendo.data.Model {
    value: string;
}

export default class TelerikHelpers {
    static adjustDropDownWidth(e) {
        const listContainer = e.sender.list.closest(".k-list-container");
        const spaceApplyed = listContainer.attr("addSpace");
        let additionalSpace = 0;
        if (!spaceApplyed) {
            additionalSpace = 160 + kendo.support.scrollbar();
            listContainer.attr("addSpace", "true");
        }
        listContainer.width(listContainer.width() + additionalSpace);
    }

    static valueMapperToDataItemMvc(options, url, valueField = 'Id') {
        if (options.value == null || options.value === '') {
            options.success(null);
            return;
        }
        const values = [];
        if (Array.isArray(options.value)) {
            options.value.forEach((entry) => {
                values.push(entry)
            });
        } else {
            values.push(options.value);
        }
        const filter = values.map((v) => {
            if (v === null) return `${valueField}~eq~null`;
            if (typeof v === 'string') {
                const escaped = v.replace(/'/g, "\\'");
                return `${valueField}~eq~'${escaped}'`;
            }
            return `${valueField}~eq~${v}`;
        }).join('~or~');
        CommonFunctions.getWithData(url, { filter, page: 1, pageSize: values.length }, (result) => {
            const data = result?.Data ?? result;
            if (Array.isArray(data)) {
                options.success(data.length > 1 ? data : data[0]);
            } else {
                options.success(data);
            }
        })
    }

    static findClosestGridDataItem(element: JQuery) {
        const uid = element.closest("[data-uid]").data("uid");
        const dataSource = element.closest(".k-grid").data("kendoGrid").dataSource;
        return dataSource.getByUid(uid);
    }

    static findClosestDropDownTreeDataItem(element: JQuery): DropdownModel {
        const uid = element.closest("[data-uid]").data("uid");
        const dataSource = element.closest(".k-treeview").data("kendoTreeView").dataSource;
        return dataSource.getByUid(uid) as DropdownModel;
    }

    static gridExpandToFit(grid: kendo.ui.Grid) {
        if (grid.thead === null) {
            return;
        }
        const $gridHeaderTable = grid.thead.closest('table');
        const gridDataWidth = $gridHeaderTable.width();
        const gridWrapperWidth = $gridHeaderTable.closest('.k-grid-header-wrap').outerWidth(true);
        // Since this is called after column auto-fit, reducing size
        // of columns would overflow data.
        if (gridDataWidth >= gridWrapperWidth) {
            return;
        }

        //find no resize indexes
        const $headers = $gridHeaderTable.find("thead > tr[role='row'] > th");
        const noResizeIndexes = [];
        $headers.each((index, elem) => {
            if (elem.attributes && elem.attributes["data-noresize"]) {
                noResizeIndexes.push(index);
            }
        });

        const $headerCols = $gridHeaderTable.find('colgroup > col');
        const $tableCols = grid.table.find('colgroup > col');
        const $footerCols = grid.wrapper.find(".k-grid-footer colgroup > col");
        let $widthOfNoResizeItems = 0;
        //get width of no resize items
        $headerCols.not('.k-group-col').each((index, elem) => {
            if ($.inArray(index, noResizeIndexes) !== -1) {
                $widthOfNoResizeItems += $(elem).width();
            }
        });

        const remainingWidth = gridWrapperWidth - $widthOfNoResizeItems;

        function applyNewWidth(index, elem) {
            if ($.inArray(index, noResizeIndexes) === -1) {
                const originalWidth = $(elem).width();
                const newWidth = Math.floor((originalWidth / (gridDataWidth - $widthOfNoResizeItems)) * remainingWidth);
                if (newWidth !== 0) {
                    $(elem).css({
                        width: newWidth
                    });
                }
            }
        }

        $headerCols.not('.k-group-col').each(applyNewWidth);
        $tableCols.not('.k-group-col').each(applyNewWidth);
        $footerCols.not('.k-group-col').each(applyNewWidth);
    }

    static gridFitColumns(columns: kendo.ui.GridColumn[], grid: kendo.ui.Grid) {
        columns.forEach((col) => {
            if (col.columns) {
                TelerikHelpers.gridFitColumns(col.columns, grid);
            } else if (!col.attributes || !col.attributes["data-noresize"]) {
                grid.autoFitColumn(col);
            }
        });
        TelerikHelpers.gridExpandToFit(grid);
    }

    static adjustGridHeight(grid: kendo.ui.Grid) {
        if (window.innerWidth <= 799 || window.innerHeight <= 500) {
            return; // small screens don't need to adjust height
        }
        if (!grid || !grid.wrapper || !grid.wrapper[0]) {
            return;
        }
        const isInDetailPage = $("body").find(".detailpageHeader").length === 1;
        const isInWindow = grid.element.closest(".k-window-content").length === 1;
        const hasNoResizeClass = grid.element.closest(".noAutoResize").length === 1;
        if (isInDetailPage || isInWindow || hasNoResizeClass) {
            return; // no height adjust for grids in detail pages or grids in window
        }

        const selectors = ['.header-wrapper', '.gridPageTitle', '.k-tabstrip-items-wrapper.k-hstack'];
        let totalHeight = 85; // ".navigationAndCommandBarGrid > .commandBar" + padding

        selectors.forEach((selector) => {
            const elements = document.querySelectorAll(selector) as NodeListOf<HTMLElement>;

            totalHeight += Array.from(elements).reduce((sum, element) => {
                if (element.closest('.k-tabstrip-content[aria-hidden="true"]') || element.offsetParent === null) {
                    return sum; // Überspringe unsichtbare Elemente
                }

                const computedStyle = window.getComputedStyle(element);
                const marginTop = parseFloat(computedStyle.getPropertyValue("margin-top")) || 0;
                const marginBottom = parseFloat(computedStyle.getPropertyValue("margin-bottom")) || 0;
                const paddingTop = parseFloat(computedStyle.getPropertyValue("padding-top")) || 0;
                const paddingBottom = parseFloat(computedStyle.getPropertyValue("padding-bottom")) || 0;
                return sum + element.getBoundingClientRect().height + marginTop + marginBottom + paddingTop + paddingBottom;
            }, 0);
        });

        const viewportHeight = window.innerHeight;
        grid.wrapper[0].style.height = `${viewportHeight - totalHeight}px`;
        grid.resize();

        const content = grid.wrapper.find(".k-grid-content")[0];
        const headerWrap = grid.wrapper.find(".k-grid-header-wrap")[0];

        if (content && headerWrap) {
            const scrollbarWidth = content.offsetWidth - content.clientWidth;
            headerWrap.style.marginRight = `${scrollbarWidth}px`;
        }
    }

    static enableDropDownToolTip(e) {
        const inputControl = e.sender.element;
        const tooltipUrl = inputControl.data("tooltip-action");
        $(e.sender.tree).kendoTooltip({
            filter: ".dropDownTreeToolTip",
            content: {
                url: tooltipUrl
            },
            width: 200,
            height: 200,
            position: "right",
            requestStart: (ev) => {
                const item = TelerikHelpers.findClosestDropDownTreeDataItem(ev.target);
                ev.options.url = tooltipUrl.replace("-1", item.value);
            }
        });
    }

    static dropDownTreeSelect(e) {
        const dataItem = e.sender.dataItem(e.node);
        if ((dataItem.Selectable !== undefined && !dataItem.Selectable) || (dataItem.value !== undefined && dataItem.value === '')) {
            e.preventDefault();
        }
    }

    static dropDownOpen(e) {
        e.sender.dataSource._data.forEach((value) => {  
            if (value.value !== undefined && value.value === '') {
                const element = $(`li[data-uid='${value.uid}']`);
                if (element.attr("data-processed") === undefined) {
                    element.find(".k-checkbox-wrapper").first().remove();
                    element.attr("data-processed", "true");
                }
            }
        });
    }
}
